import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import SignIn from "./SignIn";
import Legal from "../routes/Legal";
import { AppShellSkeleton } from "./Skeletons";
import PasswordRecovery from "./PasswordRecovery";

interface Props {
  children: ReactNode;
}

// Routes that bypass the auth gate — Apple/Google require Privacy + Terms to
// be reachable without an account, and any link from SignIn would otherwise
// dead-end on the SignIn screen.
const PUBLIC_PATHS = ["/legal", "/legal/privacy", "/legal/terms"];

export default function AuthGate({ children }: Props) {
  const { session, loading, enabled, isPasswordRecovery } = useAuth();
  const location = useLocation();

  if (!enabled) {
    return (
      <div className="p-6 max-w-md mx-auto text-slate-300">
        <div className="p-4 rounded-lg bg-amber-950 border border-amber-800 text-amber-100">
          <div className="font-semibold mb-1">Supabase not configured</div>
          <div className="text-sm">
            Set <code>VITE_SUPABASE_URL</code> and <code>VITE_SUPABASE_ANON_KEY</code> in your environment.
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <AppShellSkeleton />;
  }

  // Password recovery takes precedence over everything — even if there's a
  // session (Supabase creates one for the recovery link), the user must set
  // a new password before they can use the app.
  if (isPasswordRecovery) {
    return <PasswordRecovery />;
  }

  if (!session) {
    // Allow Privacy / Terms to render without an account.
    if (PUBLIC_PATHS.includes(location.pathname)) {
      return <Legal />;
    }
    return <SignIn />;
  }

  return <>{children}</>;
}
